import { $lang, getTagFromNode, useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  DateTimeFieldAccuracy,
  DateTimeFieldDefinition,
  DateTimeValue,
  FormFieldDefinition,
  FormOpenMode,
} from '@msdk/definitions'
import { DatePickerUnknown, DatePickerUnknownAccuracy, FormField } from '@msdk/ui'
import React from 'react'
import { useParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { useFieldLabel, useFieldSeverity, useFieldValue, useHasMandatoryValidation } from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { nodeHasFlag } from '../../../utils'
import { ValidationMessages } from '../../ValidationMessages'

function toDateTimeFieldAccuracy(accuracy: DatePickerUnknownAccuracy): DateTimeFieldAccuracy {
  switch (accuracy) {
    case 'unknown':
      return DateTimeFieldAccuracy.Unknown
    case 'year':
      return DateTimeFieldAccuracy.Year
    case 'month':
      return DateTimeFieldAccuracy.YearMonth
    case 'day':
      return DateTimeFieldAccuracy.Date
  }
}

function fromDateTimeFieldAccuracy(accuracy?: DateTimeFieldAccuracy): DatePickerUnknownAccuracy {
  switch (accuracy) {
    case DateTimeFieldAccuracy.Year:
      return 'year'
    case DateTimeFieldAccuracy.YearMonth:
      return 'month'
    case DateTimeFieldAccuracy.Date:
      return 'day'
    default:
      return 'unknown'
  }
}

export function DateTimeFieldUnknown({ node, onChange, fieldArgs }: FieldProps) {
  const fd = node.value.fieldDefinition as DateTimeFieldDefinition
  const formDef = node.value.formDefinition as FormFieldDefinition
  const fieldLabel = useFieldLabel(fd, formDef)

  const { moduleName } = useParams()
  const locale = useRecoilValue($lang)

  const t = useTranslation()
  const [value, setValue] = useFieldValue(node, onChange)

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fd.entityName)

  const onFieldChange = (d: Date | null, accuracy: DateTimeFieldAccuracy) => {
    const fieldValue = d !== null ? new DateTimeValue(d, accuracy, fd.behaviour) : null
    node.value.isDirty = true
    setValue(fieldValue)
  }

  const isReadOnly = node.value.state === 'Readonly' || fieldArgs?.formOpenMode === FormOpenMode.Readonly
  const severity = useFieldSeverity(fd, node)

  const dateFieldValue = value as DateTimeValue | null

  return (
    <FormField
      required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
      size="small"
      data-test-id={getTagFromNode(fd, moduleName)}
      severity={severity}
      readOnly={isReadOnly}
      disabled={isReadOnly}
    >
      <DatePickerUnknown
        label={fieldArgs?.showLabel && fieldLabel}
        disabled={isReadOnly}
        onChange={(date, accuracy) => onFieldChange(date as unknown as Date, toDateTimeFieldAccuracy(accuracy))}
        locale={locale}
        value={dateFieldValue?.value ?? null}
        accuracy={fromDateTimeFieldAccuracy(dateFieldValue?.accuracy)}
        labels={{
          setToday: t('CMPSR_DATE_SET_TODAY_LABEL', 'Set today'),
          setCurrentMonth: t('CMPSR_DATE_SET_CURRENT_MONTH_LABEL', 'Set current month'),
          setCurrentYear: t('CMPSR_DATE_SET_CURRENT_YEAR_LABEL', 'Set current year'),
          calendarClose: t('CMPSR_DATE_CLOSE_LABEL', 'OK'),
          calendarCancel: t('CMPSR_DATE_CANCEL_LABEL', 'Cancel'),
        }}
      />

      <ValidationMessages messages={node.value.validationMessages} fieldDef={fd} node={node} />
    </FormField>
  )
}
